import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Plus } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import AppShell from "@/components/layout/AppShell";
import { getBrickSeriesForOrganization } from "../../utils";
import useOrganization from "../../hooks/useOrganization";
import { useSession } from "../../session/SessionContext";
import { isOperator } from "../../session/access";
import OrganizationBrickSeries, {
  type BrickSeriesSummary,
} from "./OrganizationBrickSeries";
import CreateSeriesModal from "./CreateSeriesModal";
import Members from "./Members";

const OrganizationDashboard: React.FC = () => {
  const organizationId = useOrganization();
  const { user } = useSession();
  const operator = isOperator(user);
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = operator && searchParams.get("tab") === "members" ? "members" : "series";
  const [brickSeries, setBrickSeries] = useState<BrickSeriesSummary[] | null>(null);
  const [creating, setCreating] = useState(false);

  const fetchBricks = useCallback(async () => {
    try {
      setBrickSeries(await getBrickSeriesForOrganization(organizationId));
    } catch (e) {
      toast.error((e as Error).message);
      setBrickSeries([]);
    }
  }, [organizationId]);

  useEffect(() => {
    setBrickSeries(null);
    fetchBricks();
  }, [fetchBricks]);

  const total = brickSeries?.length ?? 0;
  const bricks = brickSeries?.reduce((sum, c) => sum + (c.bricks?.length ?? 0), 0) ?? 0;
  const visible = brickSeries?.filter((c) => !c.hidden).length ?? 0;

  const seriesPanel = (
    <OrganizationBrickSeries
      organizationId={organizationId}
      brickSeries={brickSeries}
      onCreate={() => setCreating(true)}
    />
  );

  return (
    <AppShell breadcrumbs={[{ label: tab === "members" ? "Leden" : "Collecties" }]}>
      <div className="mx-auto flex w-full max-w-7xl flex-col gap-6 p-4 md:p-6">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">
              {tab === "members" ? "Leden" : "Collecties"}
            </h1>
            <p className="text-sm text-muted-foreground">
              {tab === "members"
                ? "Wie deze organisatie mag beheren."
                : brickSeries == null
                  ? "Collecties laden…"
                  : `${total} ${total === 1 ? "collectie" : "collecties"} · ${bricks} stenen · ${visible} zichtbaar in de widget`}
            </p>
          </div>
          {tab === "series" && (
            <Button onClick={() => setCreating(true)}>
              <Plus />
              Nieuwe collectie
            </Button>
          )}
        </div>

        {operator ? (
          <Tabs
            value={tab}
            onValueChange={(value) =>
              setSearchParams(value === "members" ? { tab: "members" } : {}, { replace: true })
            }
          >
            <TabsList>
              <TabsTrigger value="series">Collecties</TabsTrigger>
              <TabsTrigger value="members">Leden</TabsTrigger>
            </TabsList>
            <TabsContent value="series">{seriesPanel}</TabsContent>
            <TabsContent value="members">
              <Members />
            </TabsContent>
          </Tabs>
        ) : (
          seriesPanel
        )}
      </div>
      <CreateSeriesModal
        open={creating}
        onClose={() => {
          fetchBricks();
          setCreating(false);
        }}
      />
    </AppShell>
  );
};

export default OrganizationDashboard;
